import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { courseAPI, enrollmentAPI, certificationAPI } from '../../services/api';
import { LoadingSpinner, EmptyState } from '../../components/UI';

const BrowseCourses = () => {
  const [courses, setCourses] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [certMap, setCertMap] = useState({}); // courseId -> certification
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        courseAPI.list({ search, level }),
        enrollmentAPI.mine(),
      ]);
      setCourses(c.data);
      setMyEnrollments(e.data);

      // Fetch certification for each course in parallel
      const certResults = await Promise.allSettled(
        c.data.map((course) => certificationAPI.getByCourse(course._id))
      );
      const map = {};
      certResults.forEach((r, i) => {
        if (r.status === 'fulfilled') map[c.data[i]._id] = r.value.data;
      });
      setCertMap(map);
    } catch (err) {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, level]);

  const enrolledIds = myEnrollments.map((e) => e.course?._id);

  const handleEnroll = async (courseId) => {
    try {
      await enrollmentAPI.enroll(courseId);
      toast.success('Enrollment request sent! Awaiting admin approval.');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Enrollment failed');
    }
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>Course Catalog</h1>
          <p className="subtitle">Explore courses across categories</p>
        </div>
      </div>

      <div className="flex gap-2 mb-2">
        <input
          className="input"
          placeholder="Search courses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <select
          className="select"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value="">All levels</option>
          <option>Beginner</option>
          <option>Intermediate</option>
          <option>Advanced</option>
        </select>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : courses.length === 0 ? (
        <EmptyState icon="📚" title="No courses found" />
      ) : (
        <div className="grid grid-3">
          {courses.map((c) => {
            const isEnrolled = enrolledIds.includes(c._id);
            const cert = certMap[c._id];
            return (
              <div key={c._id} className="course-card">
                <div className="thumb">{c.title.charAt(0)}</div>
                <div className="body">
                  <span className="category">
                    {c.category} · {c.level}
                  </span>
                  <h3>{c.title}</h3>
                  <p className="desc">{c.description}</p>
                  {cert && (
                    <div
                      className="mt-1"
                      style={{
                        background: cert.accentColor + '15',
                        border: `1px solid ${cert.accentColor}40`,
                        padding: '0.5rem 0.75rem',
                        borderRadius: 8,
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      <span style={{ fontSize: '1.2rem' }}>🏅</span>
                      <div>
                        <strong style={{ color: cert.accentColor }}>{cert.title}</strong>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {cert.validityMonths ? `${cert.validityMonths}-month` : 'Lifetime'} validity ·
                          {' '}Min {cert.minScore}%
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="meta">
                    <span>👤 {c.trainer?.name || 'TBA'}</span>
                    <span>${c.price || 'Free'}</span>
                  </div>
                  <button
                    className="btn btn-primary mt-2"
                    style={{ width: '100%' }}
                    onClick={() => handleEnroll(c._id)}
                    disabled={isEnrolled}
                  >
                    {isEnrolled ? 'Already enrolled' : 'Enroll →'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrowseCourses;
